import { BaseTable, Card, HeaderMenu, Loading, PaginationData, TableNumber } from "components"
import { Filter } from "pages/Inventori/Transaction/SetStockByLocation/components"
import { FilterTypeSetStockByLocation } from "pages/Inventori/Transaction/SetStockByLocation/types"
import { Fragment, useMemo, useState } from "react"
import { convertNumber, default_pagination_value } from "utils"
import { useListDetailStockLocation } from "./hooks"
import { PicturesSection } from "pages/Inventori/Register/ItemGroup/components"
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa6";

export default function ItemDetailStockLocation() {

    // state
    const [filter, setFilter] = useState<FilterTypeSetStockByLocation>({
        ...default_pagination_value,
        zero_soh: true,
        zero_min: true,
        zero_max: true,
    })

    const [sort, setSort] = useState<{
        location: string | null
        field: "soh" | "min" | "max" | "div" | null
        direction: "asc" | "desc"
    }>({
        location: null,
        field: null,
        direction: "asc",
    })

    const { data, isLoading } = useListDetailStockLocation(filter)

    const locationNames = data?.items?.length
        ? Object.keys(data.items[0].locations ?? {})
        : []



    const sortedItems = useMemo(() => {
        if (!sort.location || !sort.field) return data?.items ?? []

        const location = sort.location
        const field = sort.field

        return [...(data?.items ?? [])].sort((a, b) => {
            const aVal = Number(a.locations?.[location]?.[field] ?? 0)
            const bVal = Number(b.locations?.[location]?.[field] ?? 0)

            return sort.direction === "asc"
                ? aVal - bVal
                : bVal - aVal
        })
    }, [data?.items, sort])



    return (
        <section className="container my-5">
            <HeaderMenu title="ITEM DETAIL STOCK BY LOCATION" className="sticky top-[32px] z-[1]" />

            <section className="my-2">
                <section className="mt-2 sticky top-[120px] z-[1]">
                    <Card>
                        <Card.Body>
                            <Filter
                                filter={filter}
                                setFilter={setFilter}
                                isList
                            />
                        </Card.Body>
                    </Card>
                </section>
            </section>

            <section className="mt-2">
                <Card>
                    <Card.Body>
                        <div className="overflow-x-auto max-h-[calc(100vh-600px)]">
                            <BaseTable className="border-collapse">
                                <thead className="sticky top-[200px] z-[3] !bg-black !text-white">
                                    <tr>
                                        <th rowSpan={2}>NO</th>
                                        <th rowSpan={2}>CATEGORY</th>
                                        <th rowSpan={2}>PICTURE</th>
                                        <th rowSpan={2}>BSI</th>
                                        <th rowSpan={2}>BUILD</th>
                                        <th rowSpan={2}>BARCODE</th>
                                        <th rowSpan={2}>PRODUCT</th>
                                        <th rowSpan={2}>UNIT(inventory)</th>
                                        {locationNames.map(loc => (
                                            <th key={loc} colSpan={4} className="text-center">
                                                {loc}
                                            </th>
                                        ))}
                                    </tr>

                                    <tr>
                                        {locationNames.map(loc => (
                                            <Fragment key={loc}>
                                                {["soh", "min", "max", "div"].map(field => {
                                                    const isActive =
                                                        sort.location === loc && sort.field === field

                                                    return (
                                                        <th
                                                            key={`${loc}-${field}`}
                                                            onClick={() => {
                                                                setSort(prev => ({
                                                                    location: loc,
                                                                    field: field as any,
                                                                    direction:
                                                                        prev.location === loc &&
                                                                            prev.field === field &&
                                                                            prev.direction === "asc"
                                                                            ? "desc"
                                                                            : "asc",
                                                                }))
                                                            }}
                                                            className="cursor-pointer select-none text-center"
                                                        >
                                                            <div className="flex items-center justify-center gap-1">
                                                                {field.toUpperCase()}


                                                                {isActive ? (
                                                                    <span className="text-xs">
                                                                        {sort.direction === "asc" ? <FaSortUp size={12}/> : <FaSortDown size={12}/>}
                                                                    </span>
                                                                ): (
                                                                    <span className="text-xs">
                                                                        <FaSort size={12}/>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </th>
                                                    )
                                                })}
                                            </Fragment>
                                        ))}
                                    </tr>

                                </thead>

                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan={locationNames.length * 4 + 8} className="text-center">
                                                <Loading loading errorText="Loading..." />
                                            </td>
                                        </tr>
                                    ) : data.items.length === 0 ? (
                                        <tr>
                                            <td colSpan={locationNames.length * 4 + 8} className="text-center">
                                                No data available
                                            </td>
                                        </tr>
                                    ) : (
                                        sortedItems?.map((item, index) => {
                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        {TableNumber({
                                                            dataLength: data.items?.length,
                                                            pageCurrent: filter.page,
                                                            index,
                                                            limit: filter.limit,
                                                            totalData: filter.total,
                                                        })}
                                                    </td>
                                                    <td>{item.category_name}</td>
                                                    <td className="!w-[80px]">
                                                        <div className="flex justify-center">
                                                            {Boolean(item?.pictures?.length) && (
                                                                <PicturesSection
                                                                    dataPicture={item.pictures}
                                                                    items={1}
                                                                    className="size-[50px] my-[-10px]"
                                                                />
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {`${item.buy ? "B" : ""}${item.sell ? "S" : ""}${item.inventory ? "I" : ""}`}
                                                    </td>
                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            className="checkbox checkbox-primary"
                                                            checked={item.build_item}
                                                            disabled
                                                        />
                                                    </td>
                                                    <td>{item.product_barcode}</td>
                                                    <td>{item.product_name}</td>
                                                    <td>{item.mr_unit_name}</td>
                                                    {locationNames.map(loc => {

                                                        const textColor =
                                                            (Number(item.locations[loc]?.div ?? 0) > 0)
                                                                ? "text-green-500"
                                                                : (Number(item.locations[loc]?.div ?? 0) < 0)
                                                                    ? "text-red-500"
                                                                    : ""

                                                        return (
                                                            <Fragment key={loc}>
                                                                <td className="text-center">{convertNumber(item.locations[loc]?.soh ?? 0).intoNormalAmount}</td>
                                                                <td className="text-center">{convertNumber(item.locations[loc]?.min ?? 0).intoNormalAmount}</td>
                                                                <td className="text-center">{convertNumber(item.locations[loc]?.max ?? 0).intoNormalAmount}</td>
                                                                <td className={`text-center ${textColor} font-semibold`}>{convertNumber(item.locations[loc]?.div ?? 0).intoNormalAmount}</td>
                                                            </Fragment>
                                                        )
                                                    })}
                                                </tr>
                                            )
                                        })
                                    )}
                                </tbody>
                            </BaseTable>
                        </div>
                        <div className="mt-5">
                            <PaginationData
                                data={data}
                                pagination={filter}
                                setPagination={setFilter}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </section>
        </section>
    )
}